import { cn } from '@siliconx/shared/ui/utils';

/**
 * SIL-305 (W4, financials-hifi-flow-spec.md slice 3) - TELEMETRY and
 * FINANCIALS status badges for the assets registry table. Both statuses are
 * derived server-side (GET /api/client/assets) - these components are purely
 * presentational, mirroring ConditionBadge / LifecycleBadge's dot-and-pill
 * convention (packages/shared/src/ui).
 */

export type TelemetryStatus = 'live' | 'dark' | 'no_data';

/** "live" dot / dark - a live GPU server pings within
 * STALE_TELEMETRY_DAYS_THRESHOLD days; 'dark' means it went quiet; 'no_data'
 * means it has never reported (e.g. no telemetry agent configured). */
export function TelemetryStatusDot({ status }: { status: TelemetryStatus }) {
  const label = status === 'live' ? 'Live' : status === 'dark' ? 'Dark' : 'No data';
  const dotColor =
    status === 'live' ? 'bg-emerald-500' : status === 'dark' ? 'bg-zinc-800' : 'bg-zinc-300';
  const textColor =
    status === 'live' ? 'text-emerald-700' : status === 'dark' ? 'text-zinc-700' : 'text-zinc-400';
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs font-medium', textColor)}>
      <span className={cn('size-2 rounded-full', dotColor)} aria-hidden />
      {label}
    </span>
  );
}

export type FinancialsStatus = 'complete' | 'missing';
export type FinancialsSource = 'manual' | 'csv' | 'erp' | null;

const SOURCE_LABELS: Record<'manual' | 'csv' | 'erp', string> = {
  manual: 'Manual',
  csv: 'CSV import',
  erp: 'ERP',
};

/** "COMPLETE - SAP" (source system when known, else the closed source enum
 * label) or "MISSING ->" (arrow signals the missing-financials filter /
 * bulk-entry affordance the caller should reach for). */
export function FinancialsStatusBadge({
  status,
  source,
  sourceSystem,
}: {
  status: FinancialsStatus;
  source: FinancialsSource;
  sourceSystem: string | null;
}) {
  if (status === 'missing') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
        MISSING <span aria-hidden>&rarr;</span>
      </span>
    );
  }
  const sourceLabel = sourceSystem ? sourceSystem.toUpperCase() : source ? SOURCE_LABELS[source] : null;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700">
      <span className="inline-block size-1.5 rounded-full bg-emerald-500" aria-hidden />
      COMPLETE{sourceLabel ? ` - ${sourceLabel}` : ''}
    </span>
  );
}
